import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { OptimizedImage } from '../components/ui/OptimizedImage';
import { 
  Home, 
  Wrench, 
  Droplet, 
  Zap, 
  Shield, 
  Star,
  Phone,
  CheckCircle,
  ArrowRight,
  Play,
  Image as ImageIcon
} from 'lucide-react';

const ResidentialPlumbing = () => {
  const navigate = useNavigate();
  
  const handleContactClick = () => {
    navigate('/#contact');
    // Small delay to ensure navigation happens first
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  useEffect(() => {
    // Meta tags are now handled by static generation in generate-static.cjs
    // This ensures consistency and avoids conflicts between React and static HTML

    // Add Residential Plumbing Service Schema for AI SEO
    const residentialServiceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://colorado-plumbing.com/residential-plumbing#service',
      'name': 'Residential Plumbing Services',
      'description': 'Comprehensive residential plumbing services in Denver Metro including water heater installation, fixture repair, pipe replacement, and emergency plumbing. Master plumber owned with 300+ completed projects.',
      'provider': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'email': 'codee@colorado-plumbing.com',
        'url': 'https://colorado-plumbing.com',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Highlands Ranch',
          'addressRegion': 'CO',
          'addressCountry': 'US'
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '360',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      'areaServed': [
        {
          '@type': 'City',
          'name': 'Denver',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Highlands Ranch',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Littleton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Lakewood',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Arvada',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Thornton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Longmont',
          'addressRegion': 'CO'
        }
      ],
      'serviceType': 'Residential Plumbing',
      'category': 'Plumbing Services',
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Residential Plumbing Services',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Water Heater Installation & Repair',
              'description': 'Traditional and tankless water heater installation, repair, and replacement services'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Fixture Installation & Repair',
              'description': 'Professional installation and repair of toilets, faucets, sinks, tubs, and showers'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Pipe Repair & Replacement',
              'description': 'Water line, sewer line, and gas line pipe repair and replacement services'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Emergency Plumbing Services',
              'description': '24/7 emergency residential plumbing response with fast service times'
            }
          }
        ]
      },
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'USD',
        'availability': 'https://schema.org/InStock',
        'description': 'Professional residential plumbing services with transparent pricing and master plumber expertise'
      },
      'additionalProperty': [
        {
          '@type': 'PropertyValue',
          'name': 'Emergency Service',
          'value': '24/7 Available'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Master Plumber Owned',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': '',
          'value': ''
        },
        {
          '@type': 'PropertyValue',
          'name': 'Pricing Transparency',
          'value': 'Complete pricing guide online'
        }
      ]
    };

    // Inject the service schema
    let serviceSchemaScript = document.querySelector('#residential-service-schema');
    if (serviceSchemaScript) {
      serviceSchemaScript.textContent = JSON.stringify(residentialServiceSchema);
    } else {
      serviceSchemaScript = document.createElement('script');
      serviceSchemaScript.setAttribute('id', 'residential-service-schema');
      serviceSchemaScript.setAttribute('type', 'application/ld+json');
      serviceSchemaScript.textContent = JSON.stringify(residentialServiceSchema);
      document.head.appendChild(serviceSchemaScript);
    }
  }, []);

  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950">
      <Header />
      <main>
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10 py-16 md:py-24">
            <div className="inline-block px-4 py-2 mb-6 rounded-full bg-trust-light text-trust-deep font-semibold text-sm border border-trust-primary/20">
              <Home className="inline-block mr-2" size={20} />
              Denver's Premier Residential Plumbing Experts
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-neutral-white">
              Complete Home Plumbing Solutions
            </h1>
            <p className="text-xl mb-8 text-trust-light/90">
              From fixture installation to <Link to="/sewer-repair-replacement" className="text-action-warm hover:text-white underline">sewer line replacement</Link>, our master plumbers deliver exceptional service with <Link to="/pricing" className="text-action-warm hover:text-white underline">transparent pricing</Link>.
            </p>
            
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                <p>Reviews loading... See our 500+ verified reviews on Google and BirdEye.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <button 
                data-token="d42111216a654eb1a935ffcd97b30355" 
                data-orgname="Colorado-First-Plumbing-and-Sewer-LLC" 
                className="hcp-button bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                onClick={() => {
                  if (window.HCPWidget) {
                    window.HCPWidget.openModal();
                  }
                }}
              >
                Get Free Estimate
              </button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-blue-900 to-blue-950 relative">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Complete Residential Plumbing Services</h2>
              <p className="text-lg text-blue-200">
                From emergency repairs to full home <Link to="/bathroom-kitchen-remodel" className="text-yellow-300 underline hover:text-orange-300">remodeling projects</Link>, 
                our experienced team handles every aspect of residential plumbing with professional expertise.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Water Heater Services */}
              <div className="group bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-2xl hover:border-blue-400/50 transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 w-fit mb-4 shadow-md">
                  <Zap className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Water Heater Services</h3>
                <p className="text-blue-200 mb-4">Traditional and tankless water heater installation, repair, and replacement.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Traditional Water Heater Install</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Tankless Water Heater Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Water Heater Repair & Maintenance</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Emergency Water Heater Replacement</span>
                  </li>
                </ul>
              </div>

              {/* Fixture Installation & Repair */}
              <div className="group bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-2xl hover:border-blue-400/50 transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 w-fit mb-4 shadow-md">
                  <Wrench className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Fixture Installation & Repair</h3>
                <p className="text-blue-200 mb-4">Professional installation and repair of all plumbing fixtures.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Toilet Installation & Repair</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Faucet & Sink Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Tub & Shower Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Garbage Disposal Replacement</span>
                  </li>
                </ul>
              </div>

              {/* Pipe Services */}
              <div className="group bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20 hover:shadow-2xl hover:border-blue-400/50 transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-3 rounded-lg bg-gradient-to-r from-red-500 to-red-600 w-fit mb-4 shadow-md">
                  <Droplet className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">Pipe Repair & Replacement</h3>
                <p className="text-blue-200 mb-4">Comprehensive pipe services for water, sewer, and gas lines.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Domestic Water Line Repair</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">
                      <Link to="/sewer-repair-replacement" className="text-trust-primary hover:text-action-primary">Sewer Drainage Repair</Link>
                    </span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Gas Piping Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-gray-200">Pipe Leak Detection & Repair</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Competitive Advantages */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container-custom">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Choose Colorado First Plumbing?</h2>
              <p className="text-lg text-trust-light/90">
                We're the only residential plumbing company in Denver Metro with this level of experience, reviews, and transparency. 
                Our commitment to quality and honesty sets us apart from the competition.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Transparent Pricing */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-gradient-to-r from-success-primary to-success-eco w-fit mb-4 shadow-md">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Complete Transparent Pricing</h3>
                <p className="text-trust-light/90 mb-4">
                  We're the only residential plumbing company in Denver willing to show you our <Link to="/pricing" className="text-action-warm underline hover:text-white">complete pricing guide online</Link>. 
                  No hidden fees, no surprises, just honest pricing you can trust.
                </p>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="font-semibold">✓ All prices listed online</p>
                  <p className="font-semibold">✓ No hidden fees guaranteed</p>
                  <p className="font-semibold">✓ Upfront estimates before work begins</p>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Video & Photo Gallery */}
        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-950 relative">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom relative z-10">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Our Residential Work Gallery</h2>
              <p className="text-lg text-blue-200">
                See examples of our high-quality residential plumbing installations and repairs across Denver Metro.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Rough Plumbing */}
              <div className="group bg-white/10 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-white/20 hover:shadow-2xl transition-shadow">
                <OptimizedImage 
                  src="/colorado-plumbing-uploads/services/remodels/rough-plumbing.jpg" 
                  alt="Castle Rock rough-in plumbing" 
                  className="w-full h-48 object-cover" 
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Rough-In Plumbing</h3>
                  <p className="text-blue-200 text-sm">Professional rough-in plumbing installation in Castle Rock.</p>
                </div>
              </div>

              {/* Fixture Installation Gallery */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <OptimizedImage 
                  src="/colorado-plumbing-uploads/services/remodels/vanity-install-denver-remodel.JPEG" 
                  alt="Denver vanity installation" 
                  className="w-full h-48 object-cover" 
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Fixture Installation</h3>
                  <p className="text-blue-200 text-sm">Professional vanity installation in a Denver remodel.</p>
                </div>
              </div>

              {/* Pipe Repair Projects */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <OptimizedImage 
                  src="/colorado-plumbing-uploads/existing-projects/gas-pipe-install-lakewood-colorado-first-plumbing.png" 
                  alt="Gas pipe installation in Lakewood" 
                  className="w-full h-48 object-cover" 
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Gas Pipe Installation</h3>
                  <p className="text-blue-200 text-sm">A certified gas pipe installation for a happy Lakewood customer.</p>
                </div>
              </div>

              {/* Emergency Repairs */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <OptimizedImage 
                  src="/colorado-plumbing-uploads/services/sewer-services/sewer-snake-highlands-ranch-cost.JPEG" 
                  alt="Sewer snake service in Highlands Ranch" 
                  className="w-full h-48 object-cover" 
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Emergency Sewer Cleaning</h3>
                  <p className="text-blue-200 text-sm">Emergency sewer snaking to clear a stubborn clog in Highlands Ranch.</p>
                </div>
              </div>

              {/* Plumbing Service Projects */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <OptimizedImage 
                  src="/colorado-plumbing-uploads/existing-projects/best-plumbing-company-highlands-ranch.png" 
                  alt="Plumbing service in Highlands Ranch" 
                  className="w-full h-48 object-cover" 
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Trusted by Homeowners</h3>
                  <p className="text-blue-200 text-sm">Providing reliable plumbing services for homeowners in Highlands Ranch.</p>
                </div>
              </div>

              {/* Garbage Disposal */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <OptimizedImage 
                  src="/colorado-plumbing-uploads/services/sewer-services/sewer-repair-double-clean-out-thornton.JPEG" 
                  alt="Double cleanout installation" 
                  className="w-full h-48 object-cover" 
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Cleanout Installation</h3>
                  <p className="text-blue-200 text-sm">A new double cleanout installation in Thornton for easier future maintenance.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Reviews Section */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="flex items-center justify-center mb-4">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={24} />
                  ))}
                </div>
                <span className="ml-3 text-2xl font-bold">4.8/5 (293+ Reviews)</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Denver Homeowners Love Our Service</h2>
              <p className="text-lg text-trust-light/90">
                See what our residential customers have to say about our plumbing services across Denver Metro.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Review 1 - Toilet & Faucet Repair (Real BirdEye Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-action-warm">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm font-medium bg-white/20 px-2 py-1 rounded">BirdEye</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "I had two running toilets and a leaking faucet and Reese came out and fixed both with ease. He was very friendly and helpful. Also kept me in the loop the entire time. Thanks Reese!"
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Residential Customer</p>
                  <p className="text-trust-light/80">Denver Metro Homeowner</p>
                </div>
              </div>

              {/* Review 2 - Professional Service (Real BirdEye Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-action-warm">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm font-medium bg-white/20 px-2 py-1 rounded">BirdEye</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "I was very pleased with the service that I received from Colorado First Plumbing and technician, Reese. He was very efficient, knowledgeable and kind. I would use their service again in the future."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Residential Customer</p>
                  <p className="text-trust-light/80">Denver Metro Homeowner</p>
                </div>
              </div>

              {/* Review 3 - Master Plumber Quality (Real HomeAdvisor Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-action-warm">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm font-medium bg-white/20 px-2 py-1 rounded">HomeAdvisor</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "The owner is a certified master plumber that truly cares about his customers. They are quick to respond and are the best plumbers I have ever seen. They earned my full trust and only wish I could have taken them with me when I moved out if the state of Colorado. I have zero hesitation in recommending this company. They might not be the cheapest but they are the best. They did give me a disabled veterans discount."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Timothy B.</p>
                  <p className="text-trust-light/80">Colorado Homeowner</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link 
                to="/reviews"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-action-primary to-action-urgent text-white rounded-lg font-semibold hover:shadow-xl transition-all"
              >
                View All 293+ Reviews
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready for Professional Residential Plumbing?</h2>
            <p className="text-xl mb-8 text-trust-light/90">
              From routine maintenance to emergency repairs, we're Denver's trusted residential plumbing experts. 
              Get started with our <Link to="/pricing" className="text-action-warm underline hover:text-white">transparent pricing</Link> and exceptional service.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              
              <button 
                onClick={handleContactClick}
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                Schedule Free Estimate
              </button>
            </div>
            
            <div className="text-center">
                          </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ResidentialPlumbing;